import { useEffect, type ReactNode } from 'react';
import { MonitoringPanel } from './MonitoringPanel';
import { ScanComparePanel } from './ScanComparePanel';
import { ScanSidePanel } from './ScanSidePanel';
import { useMonitoringScans, type MonitoringScans } from './useMonitoringScans';
import type { DmScansQueryConfig } from './useDmScansQueries';
import type { DmScanSummary } from './types';

export interface MonitoringTabProps {
    queries: DmScansQueryConfig;
    patientId: string | null;
    dmId: number | null | undefined;
    totalAligners?: number | null;
    monitoringStatus?: string | null;
    /** The patient's current aligner, for the status tooltip (the title shows the scan's). */
    currentAligner?: number | null;
    formatScanDate?: (value: string) => string;
    emptyState?: ReactNode;
    /** Lets the host mirror the selection (card subtitles, headers…). */
    onSelectedScanChange?: (scan: DmScanSummary | null) => void;
    className?: string;
    /** Both hosts show the full feature set; these exist for narrow embeds. */
    showIndices?: boolean;
    showObservations?: boolean;
    showMorphing?: boolean;
    /** Side-by-side comparison of any two scans. */
    showCompare?: boolean;
    /** Pin to the newest scan and drop the selector — for summary surfaces. */
    pinToLatest?: boolean;
    /** Adds the expand control to the media bar. */
    allowFullscreen?: boolean;
    /** Host controls in the scan header, before the Details toggle. */
    headerActions?: ReactNode;
    /** Rendered right after the Details toggle. */
    detailsMenu?: ReactNode;
}

interface MonitoringTabViewProps extends MonitoringTabProps {
    scanState: MonitoringScans;
}

function MonitoringTabView({
    queries,
    patientId,
    totalAligners = null,
    monitoringStatus,
    currentAligner,
    formatScanDate,
    emptyState,
    className,
    showIndices = true,
    showObservations = true,
    showCompare = true,
    pinToLatest = false,
    allowFullscreen = false,
    headerActions,
    detailsMenu,
    scanState: { panel, detail }
}: MonitoringTabViewProps) {
    const sidePanel =
        showIndices || showObservations ? (
            <ScanSidePanel
                detail={detail}
                showIndices={showIndices}
                showObservations={showObservations}
            />
        ) : undefined;

    const compareView = showCompare ? (
        <ScanComparePanel
            queries={queries}
            patientId={patientId}
            scans={panel.scans}
            totalAligners={totalAligners}
            formatScanDate={formatScanDate}
            tracksAligners={panel.tracksAligners}
        />
    ) : undefined;

    return (
        <MonitoringPanel
            {...panel}
            totalAligners={totalAligners}
            monitoringStatus={monitoringStatus}
            currentAligner={currentAligner}
            sidePanel={sidePanel}
            compareView={compareView}
            showScanSelector={!pinToLatest}
            allowFullscreen={allowFullscreen}
            formatScanDate={formatScanDate}
            emptyState={emptyState}
            className={className}
            headerActions={headerActions}
            detailsMenu={detailsMenu}
        />
    );
}

function MonitoringTab(props: MonitoringTabProps) {
    const {
        queries,
        patientId,
        dmId,
        showMorphing = true,
        pinToLatest = false,
        onSelectedScanChange
    } = props;
    const scanState = useMonitoringScans({ queries, patientId, dmId, pinToLatest, showMorphing });
    const { selectedScan } = scanState.panel;

    useEffect(() => {
        onSelectedScanChange?.(selectedScan);
    }, [selectedScan, onSelectedScanChange]);

    return <MonitoringTabView {...props} scanState={scanState} />;
}

export { MonitoringTab };
